import { usePathname } from 'next/navigation';
import { useEffect, useMemo } from 'react';

import {
  ADVANCED_SEARCH_URL,
  COMPS_URL,
  COUNTY_RENT_URL,
  COUNTY_YIELD_URL,
  CROP_HISTORY_URL,
  ELEVATION_URL,
  FLOOD_URL,
  HISTORICAL_IMAGERY_URL,
  INSIGHT_URL,
  INSIGHTS_CATALOG_URL,
  LEAD_LIST_URL,
  MORTGAGE_URL,
  POWER_LINES_URL,
  PROPERTY_TAX_URL,
  SAVED_LAND_PLAT_MAP_URL,
  SOILS_URL,
  TIMBER_MILLS_URL,
  TIMBER_SITE_INDEX_URL,
  VEGETATION_INDEX_URL,
  WATER_URL,
} from '@/constants/urls';
import usePrevious from '@/hooks/usePrevious';

import { ToolMapper, ToolMenu } from '../constants';
import { useToolbarContext } from '../provider';

const insightsUrls = [
  INSIGHT_URL,
  COUNTY_RENT_URL,
  COUNTY_YIELD_URL,
  CROP_HISTORY_URL,
  ELEVATION_URL,
  FLOOD_URL,
  HISTORICAL_IMAGERY_URL,
  PROPERTY_TAX_URL,
  POWER_LINES_URL,
  SOILS_URL,
  TIMBER_MILLS_URL,
  TIMBER_SITE_INDEX_URL,
  VEGETATION_INDEX_URL,
  WATER_URL,
];

const disableMapper = {
  [ADVANCED_SEARCH_URL]: {
    [ToolMenu.Draw_Polygon]: true,
    [ToolMenu.Draw_Line]: true,
    [ToolMenu.Draw_Point]: true,
    [ToolMenu.Draw_Text]: true,
  },
  [COMPS_URL]: {
    [ToolMenu.Draw_Polygon]: true,
    [ToolMenu.Draw_Line]: true,
    [ToolMenu.Draw_Point]: true,
    [ToolMenu.Draw_Text]: true,
  },
  [COUNTY_RENT_URL]: {
    [ToolMenu.Info]: true,
    [ToolMenu.Select]: false,
  },
  [COUNTY_YIELD_URL]: {
    [ToolMenu.Info]: true,
    [ToolMenu.Select]: false,
  },
  [CROP_HISTORY_URL]: {
    [ToolMenu.Info]: true,
    [ToolMenu.Select]: false,
  },
  [ELEVATION_URL]: {
    [ToolMenu.Info]: true,
    [ToolMenu.Select]: false,
  },
  [FLOOD_URL]: {
    [ToolMenu.Info]: true,
    [ToolMenu.Select]: false,
  },
  [HISTORICAL_IMAGERY_URL]: {
    [ToolMenu.Info]: true,
    [ToolMenu.Select]: false,
  },
  [INSIGHTS_CATALOG_URL]: {
    [ToolMenu.Info]: false,
    [ToolMenu.Select]: true,
  },
  [LEAD_LIST_URL]: {
    [ToolMenu.Draw_Polygon]: true,
    [ToolMenu.Draw_Line]: true,
    [ToolMenu.Draw_Point]: true,
    [ToolMenu.Draw_Text]: true,
  },
  [MORTGAGE_URL]: {
    [ToolMenu.Draw_Polygon]: true,
    [ToolMenu.Draw_Line]: true,
    [ToolMenu.Draw_Point]: true,
    [ToolMenu.Draw_Text]: true,
  },
  [POWER_LINES_URL]: {
    [ToolMenu.Info]: false,
    [ToolMenu.Select]: true,
  },
  [SAVED_LAND_PLAT_MAP_URL]: {
    [ToolMenu.Info]: false,
    [ToolMenu.Select]: true,
    [ToolMenu.Draw_Polygon]: true,
    [ToolMenu.Draw_Line]: true,
    [ToolMenu.Draw_Point]: true,
    [ToolMenu.Draw_Text]: true,
  },
  [SOILS_URL]: {
    [ToolMenu.Info]: true,
    [ToolMenu.Select]: false,
  },
  [TIMBER_MILLS_URL]: {
    [ToolMenu.Info]: false,
    [ToolMenu.Select]: false,
  },
  [TIMBER_SITE_INDEX_URL]: {
    [ToolMenu.Info]: true,
    [ToolMenu.Select]: false,
  },
  [VEGETATION_INDEX_URL]: {
    [ToolMenu.Info]: true,
    [ToolMenu.Select]: false,
  },
  [WATER_URL]: {
    [ToolMenu.Info]: true,
    [ToolMenu.Select]: false,
  },
};

/**
 * Some pages we will want either the info or select tool to be disabled
 * @returns logic to disable tool items based on the current URL
 */
export const useDisableToolItem = () => {
  const pathname = usePathname();
  const { selectedTool, handleSwappingTool } = useToolbarContext();
  const previousPage = usePrevious(pathname);

  const previousPageIsInsight = useMemo(
    () => insightsUrls.some((url) => previousPage?.includes(url)),
    [previousPage]
  );
  const currentPageIsInsight = useMemo(
    () => insightsUrls.some((url) => pathname?.includes(url)),
    [pathname]
  );

  const disableLogic = useMemo(() => {
    const key = Object.keys(disableMapper).find((url) =>
      pathname?.includes(url)
    );

    if (key) return disableMapper[key];

    return {
      [ToolMenu.Info]: false,
      [ToolMenu.Select]: false,
    };
  }, [pathname]);

  useEffect(() => {
    if (previousPageIsInsight && !currentPageIsInsight) {
      handleSwappingTool(ToolMapper[ToolMenu.Info]);
    }
  }, [currentPageIsInsight, previousPageIsInsight, handleSwappingTool]);

  useEffect(() => {
    if (disableLogic[ToolMenu.Info] && selectedTool?.parent === ToolMenu.Info) {
      handleSwappingTool(ToolMapper[ToolMenu.Select]);
    }

    if (
      disableLogic[ToolMenu.Select] &&
      selectedTool?.parent === ToolMenu.Select
    ) {
      handleSwappingTool(ToolMapper[ToolMenu.Info]);
    }
  }, [disableLogic, selectedTool?.parent, handleSwappingTool]);

  return disableLogic;
};
